'use client';

import NavSearch from 'builder/components/elements/NavSearch';
import { SearchBarVariant } from 'builder/components/elements/NavSearchElements/searchUtils';
import { type JSX, useMemo, useState } from 'react';
import { getSearchUrl, SearchFilterContentTypes, type SearchOrigins } from 'search-gateway-client';
import { StatsigExperimentContext, type StatsigExperimentFlags } from 'statsig/components';

import type { ConversationalSearchNavFlags } from './types';

interface TopNavSearchBarProps {
    onCollapse?: () => void;
    showCloseButton?: boolean;
    conversationalSearch?: ConversationalSearchNavFlags;
}

export default function TopNavSearchBar({
    onCollapse,
    showCloseButton,
    conversationalSearch,
}: TopNavSearchBarProps): JSX.Element {
    const [showSuggestions, setShowSuggestions] = useState(false);

    // NavSearch and its toggle read conversational-search flags from
    // StatsigExperimentContext — the same channel Builder pages use — so the
    // server-evaluated permissions are provided here rather than as new props.
    const statsigFlags = useMemo<StatsigExperimentFlags>(
        () => ({
            showConversationalSearchCta: conversationalSearch?.hasAccess ?? false,
            showConversationalSearchAudio: conversationalSearch?.supportsAudio ?? false,
        }),
        [conversationalSearch?.hasAccess, conversationalSearch?.supportsAudio],
    );

    const handleSubmit = (
        contentType: SearchFilterContentTypes,
        searchValue: string,
        searchOrigin: SearchOrigins,
    ): void => {
        const searchUrl = getSearchUrl({
            contentType,
            searchTerm: searchValue,
            searchOrigin,
        });
        window.location.href = searchUrl;
    };

    return (
        <StatsigExperimentContext.Provider value={statsigFlags}>
            <NavSearch
                contentType={SearchFilterContentTypes.All_videos_content_type}
                onSubmit={handleSubmit}
                userHasAnySubscription={false}
                isEnterpriseMember={false}
                pproPluginEnabled={false}
                isSearchAppLoaded={(): boolean => true}
                imagesUrl=""
                showSuggestions={showSuggestions}
                setShowSuggestions={setShowSuggestions}
                // StockBlocks always shows Snapshots (SnapshotsFlag is hard-enabled); keep the shell nav in parity.
                useSnapshots
                searchBarVariant={SearchBarVariant.TopNav}
                className="lg:w-full"
                compact
                onCollapse={onCollapse}
                showCloseButton={showCloseButton}
            />
        </StatsigExperimentContext.Provider>
    );
}
